import { Checkbox } from "@legal-500/legal500-design-system";
import { Controller } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import type { FormFieldConfig } from "../../../types/uiTypes";
import styles from "../forms/GenericForm.module.css";

type CheckboxGroupFieldProps = {
  field: FormFieldConfig & {
    checkboxOptions: Array<{ value: string; label: string }>;
  };
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
};

export function CheckboxGroupField({ field, control, errors }: CheckboxGroupFieldProps) {
  const error = errors[field.name]?.message;
  const errorMessage = typeof error === "string" ? error : undefined;
  const groupId = `${field.name}-group`;
  const errorId = `${field.name}-error`;
  const helperId = `${field.name}-helper`;
  const describedBy = errorMessage ? errorId : field.helperText ? helperId : undefined;

  return (
    <div className={styles.formField}>
      <label id={groupId} className={styles.label}>
        {field.label}{" "}
        {field.required && (
          <span className={styles.required} aria-label="required">
            *
          </span>
        )}
      </label>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => (
          <div>
            <div
              className={styles.checkboxGroup}
              role="group"
              aria-labelledby={groupId}
              aria-describedby={describedBy}
              aria-invalid={!!errorMessage}
              aria-required={field.required}
            >
              {field.checkboxOptions.map(option => {
                const currentValue = Array.isArray(controllerField.value)
                  ? (controllerField.value as string[])
                  : [];
                return (
                  <Checkbox
                    key={option.value}
                    id={`${field.name}-${option.value}`}
                    checked={currentValue.includes(option.value)}
                    onChange={e => {
                      const newValue = e.target.checked
                        ? [...currentValue, option.value]
                        : currentValue.filter((v: string) => v !== option.value);
                      controllerField.onChange(newValue);
                    }}
                    label={option.label}
                  />
                );
              })}
            </div>
            {errorMessage && (
              <span id={errorId} className={styles.errorText} role="alert" aria-live="polite">
                {errorMessage}
              </span>
            )}
            {field.helperText && !errorMessage && (
              <span id={helperId} className={styles.helperText}>
                {field.helperText}
              </span>
            )}
          </div>
        )}
      />
    </div>
  );
}
